/**
 * IKONERNA. Varje bana är KOPIERAD ORDAGRANT ur design/appboken.html.
 *
 * De ligger i EN komponent av ett enda skäl: utan den ritar fyra agenter fyra
 * olika klockor, och startsidans kakel slutar se ut som ett system. Behövs en
 * ikon som inte finns här är det ett designbeslut — lägg till den HÄR, i
 * samma streckstil (fill none, stroke currentColor, runda ändar, 24x24-ruta),
 * så får alla den.
 *
 * FÄRGEN ÄRVS ALLTID (stroke="currentColor"). Ikonen i ett kakel är --accent
 * för att `.tile .ti` säger det, inte för att ikonen bär en färg.
 */

import type { ReactNode } from 'react';

export type Ikonnamn =
  | 'ordrar'
  | 'tid'
  | 'mil'
  | 'utlagg'
  | 'attest'
  | 'var'
  | 'inhyrda'
  | 'fakturera'
  | 'installningar'
  | 'tillbaka'
  | 'fram'
  | 'rost'
  | 'bock'
  | 'kamera'
  | 'plats'
  | 'hem';

const BANOR: Record<Ikonnamn, ReactNode> = {
  ordrar: (
    <>
      <rect x="5" y="4" width="14" height="17" rx="2.5" />
      <path d="M9 4h6v2.5H9z" />
      <path d="M9 11.5h6M9 15.5h4" />
    </>
  ),
  tid: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  mil: (
    <>
      <path d="M3.5 16.5V13l1.9-4.4A2.2 2.2 0 0 1 7.4 7.3h9.2a2.2 2.2 0 0 1 2 1.3L20.5 13v3.5" />
      <path d="M3.5 13h17" />
      <circle cx="7.3" cy="16.4" r="1.6" />
      <circle cx="16.7" cy="16.4" r="1.6" />
    </>
  ),
  utlagg: (
    <>
      <path d="M6 3.5h12v17l-2-1.4-2 1.4-2-1.4-2 1.4-2-1.4-2 1.4z" />
      <path d="M9 8.5h6M9 12.5h4" />
    </>
  ),
  attest: (
    <>
      <path d="M4 12.5l4.5 4.5L19.5 6.5" />
      <path d="M4 19.5h16" />
    </>
  ),
  var: (
    <>
      <circle cx="9" cy="8.5" r="3" />
      <path d="M3.5 19c.9-3 3-4.5 5.5-4.5S13.6 16 14.5 19" />
      <path d="M17 4.5s3.5 2.6 3.5 5.5S17 15 17 15" />
    </>
  ),
  inhyrda: (
    <>
      <rect x="3" y="7.5" width="18" height="12.5" rx="2.5" />
      <path d="M8.5 7.5V6a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1.5M3 13h18" />
    </>
  ),
  fakturera: <path d="M12 3v18M8 7h6.5a2.5 2.5 0 0 1 0 5h-5a2.5 2.5 0 0 0 0 5H16" />,
  installningar: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3.5v2M12 18.5v2M3.5 12h2M18.5 12h2M6 6l1.4 1.4M16.6 16.6L18 18M18 6l-1.4 1.4M7.4 16.6L6 18" />
    </>
  ),
  tillbaka: <path d="M15 6l-6 6 6 6" />,
  fram: <path d="M9 6l6 6-6 6" />,
  rost: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" />
    </>
  ),
  bock: <path d="M5 12.5l4.5 4.5L19 7" />,
  kamera: (
    <>
      <path d="M4 8.5h3.2l1.8-2h6l1.8 2H20V19H4z" />
      <circle cx="12" cy="13.5" r="3.4" />
    </>
  ),
  plats: (
    <>
      <path d="M12 21s7-5.4 7-11a7 7 0 1 0-14 0c0 5.6 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.4" />
    </>
  ),
  hem: (
    <>
      <path d="M3.5 11L12 4l8.5 7" />
      <path d="M5.5 9.7V19h13V9.7" />
    </>
  ),
};

export interface Ikonprops {
  readonly namn: Ikonnamn;
  /** Sidan i px. 26 i kakel, 17-18 i rader och kort, 16-20 i knappar — ett par px över prototypens, i takt med typskalan. */
  readonly storlek?: number;
  readonly tjocklek?: number;
  /**
   * Text för skärmläsare. UTELÄMNA den när ikonen sitter bredvid en etikett
   * som redan säger samma sak — då är ikonen dekoration och ska vara
   * aria-hidden, annars läser skärmläsaren allt två gånger.
   */
  readonly beskrivning?: string;
}

export const Ikon = ({ namn, storlek = 18, tjocklek = 1.8, beskrivning }: Ikonprops) => (
  <svg
    width={storlek}
    height={storlek}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={tjocklek}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...(beskrivning === undefined
      ? { 'aria-hidden': true }
      : { role: 'img', 'aria-label': beskrivning })}
  >
    {BANOR[namn]}
  </svg>
);
